import { Button } from '@trellis/roots/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@trellis/roots/dialog'
import { format } from 'date-fns'
import { Download } from 'lucide-react'
import { useState } from 'react'
import type { DateRange } from 'react-day-picker'
import { toast } from 'sonner'

import {
  useCreateReceiptBundleJobMutation,
  useReceiptBundleEstimateQuery,
} from '@/hooks/useReports'
import { formatBytes } from '@/lib/format'
import { DateRangeFilter } from '@/pages/reports/(components)/DateRangeFilter'

import { ExportJobStatus } from '../ExportJobStatus'

/**
 * Standalone "download every receipt for a date range" entry point (expenses page).
 * Reuses the reports section's DateRangeFilter for the FY/BAS-quarter presets + custom
 * range picker, then shows the pre-commit estimate before starting the job.
 */
export function ReceiptBundleButton() {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<DateRange | undefined>()
  const [jobId, setJobId] = useState<string | null>(null)

  const startDate = range?.from ? format(range.from, 'yyyy-MM-dd') : ''
  const endDate = range?.to ? format(range.to, 'yyyy-MM-dd') : ''
  const hasRange = Boolean(startDate && endDate)

  const { data: estimateResult, isFetching: isEstimating } =
    useReceiptBundleEstimateQuery({
      startDate,
      endDate,
    })
  const estimate = estimateResult?.data

  const createJob = useCreateReceiptBundleJobMutation()

  function handleOpenChange(nextOpen: boolean): void {
    setOpen(nextOpen)
    if (!nextOpen) {
      setRange(undefined)
      setJobId(null)
    }
  }

  function handleStart(): void {
    createJob.mutate(
      { startDate, endDate },
      {
        onSuccess: (data) => setJobId(data.id),
        onError: (caughtError) => {
          const message =
            caughtError instanceof Error
              ? caughtError.message
              : 'Could not start the export'
          toast.error('Could not start export', { description: message })
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Download className="size-4" />
          Download receipts
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Download receipts</DialogTitle>
          <DialogDescription>
            Bundle every receipt for a date range into one archive, with a manifest
            linking each file back to its expense.
          </DialogDescription>
        </DialogHeader>

        {jobId ? (
          <ExportJobStatus jobId={jobId} fileName="receipts.zip" />
        ) : (
          <div className="space-y-3">
            <DateRangeFilter value={range} onChange={setRange} />
            {hasRange && (
              <p className="text-muted-foreground text-sm">
                {isEstimating
                  ? 'Estimating…'
                  : estimate
                    ? `${estimate.expenseCount} expense${estimate.expenseCount === 1 ? '' : 's'} · ${formatBytes(estimate.totalSizeBytes)}`
                    : null}
              </p>
            )}
          </div>
        )}

        {!jobId && (
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleStart} disabled={!hasRange || createJob.isPending}>
              {createJob.isPending ? 'Starting…' : 'Start'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
